"use client";

import { devLabel } from "@/lib/dev-labels";
import styles from "./NarrationPanel.module.css";
import { VOICES, VOICE_CLONE_KEY } from "./narration-data";

/**
 * VoiceList — 5 preset voices + 1 Clone slot + "내 목소리 학습" CTA.
 *
 * Mockup source: docs/mockups/narration.html panel 25-A.
 * Single-select via radio semantics. Clicking the Clone slot or the
 * dashed CTA opens the VoiceCloneWizard (handled by parent).
 */

function renderStars(rating: number): string {
	const n = Math.max(0, Math.min(4, Math.round(rating)));
	return "★".repeat(n) || "—";
}

export interface VoiceListProps {
	selected: string;
	onSelect: (key: string) => void;
	onClone: () => void;
	onPreview?: (key: string) => void;
	cloneTrained?: boolean;
}

export function VoiceList({
	selected,
	onSelect,
	onClone,
	onPreview,
	cloneTrained = false,
}: VoiceListProps) {
	return (
		<div
			className={styles.rightBlock}
			data-testid="voice-list"
			{...devLabel("25-A")}
		>
			<div className={styles.rightTitle}>
				Voice 선택 ({VOICES.length} + Clone)
			</div>
			<div
				className={styles.voiceList}
				role="radiogroup"
				aria-label="Voice 선택"
			>
				{VOICES.map((voice, i) => {
					const on = selected === voice.key;
					return (
						<div
							key={voice.key}
							className={`${styles.voiceRow} ${on ? styles.voiceRowOn : ""}`}
							role="radio"
							tabIndex={0}
							aria-checked={on}
							aria-label={voice.name}
							data-testid={`voice-${voice.key}`}
							onClick={() => onSelect(voice.key)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									onSelect(voice.key);
								}
							}}
							{...devLabel(`25-A${i + 1}`)}
						>
							<div
								className={`${styles.voiceAvatar} ${styles[`voiceAvatar_${voice.avatarStyle}`]}`}
								aria-hidden="true"
							>
								{voice.initial}
							</div>
							<div className={styles.voiceInfo}>
								<div className={styles.voiceName}>
									{voice.name}{" "}
									<span className={styles.voiceGender}>· {voice.gender === "female" ? "여성" : "남성"}</span>
								</div>
								<div className={styles.voiceMeta}>{voice.description}</div>
							</div>
							<button
								type="button"
								className={styles.voicePlay}
								onClick={(e) => {
									e.stopPropagation();
									onPreview?.(voice.key);
								}}
								aria-label={`${voice.name} 미리듣기`}
								data-testid={`voice-play-${voice.key}`}
							>
								▶
							</button>
							<span className={styles.voiceStatus} aria-hidden="true">
								{renderStars(voice.rating)}
							</span>
						</div>
					);
				})}

				{/* Clone slot row */}
				<div
					className={`${styles.voiceRow} ${selected === VOICE_CLONE_KEY ? styles.voiceRowOn : ""}`}
					role="radio"
					tabIndex={0}
					aria-checked={selected === VOICE_CLONE_KEY}
					aria-label="내 목소리 (Clone)"
					data-testid="voice-clone"
					onClick={() => {
						if (cloneTrained) {
							onSelect(VOICE_CLONE_KEY);
						} else {
							onClone();
						}
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							if (cloneTrained) onSelect(VOICE_CLONE_KEY);
							else onClone();
						}
					}}
					{...devLabel(`25-A${VOICES.length + 1}`)}
				>
					<div
						className={`${styles.voiceAvatar} ${styles.voiceAvatar_clone}`}
						aria-hidden="true"
					>
						◎
					</div>
					<div className={styles.voiceInfo}>
						<div className={styles.voiceName}>
							내 목소리{" "}
							<span className={styles.mmlTag}>CLONE</span>
						</div>
						<div className={styles.voiceMeta}>
							{cloneTrained ? "학습 완료" : "미학습 — 1분 녹음 필요"}
						</div>
					</div>
					<button
						type="button"
						className={styles.voicePlay}
						onClick={(e) => {
							e.stopPropagation();
							if (cloneTrained) onPreview?.(VOICE_CLONE_KEY);
						}}
						aria-label="Clone 미리듣기"
						disabled={!cloneTrained}
						data-testid="voice-play-clone"
					>
						▶
					</button>
					<span className={styles.voiceStatus} aria-hidden="true">
						{cloneTrained ? "★★★" : "—"}
					</span>
				</div>
			</div>

			<button
				type="button"
				className={styles.cloneBtn}
				onClick={onClone}
				data-testid="open-clone-wizard"
				{...devLabel("25-A7")}
			>
				+ 내 목소리 학습 (5 코인)
			</button>
		</div>
	);
}
